/**
 * Sentences for the public error codes et-auth answers on its internal
 * routes, plus the codes the dashboard's own handlers answer. Users Config
 * and Dashboard IAM show these in toasts and result lists. A code with no
 * sentence falls back to the code itself, so a new code is visible, not
 * hidden.
 */
export const ET_AUTH_ERROR_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: "Your session ended. Sign in again.",
  FORBIDDEN: "You do not hold the tab this action needs.",
  SESSION_UNAVAILABLE: "The account service could not read your session. Try again.",
  PERMISSIONS_UNAVAILABLE: "The account service could not read the permissions. Try again.",
  LOCK_TIMEOUT: "Another change to this account is running. Try again.",
  INVALID_USER_ID: "That user id is not valid.",
  INVALID_PERMISSION: "That tab is not in the dashboard registry.",
  TARGET_NOT_FOUND: "That account does not exist.",
  TARGET_NOT_ADMIN: "That account is not a dashboard admin.",
  TARGET_IS_ADMIN: "That account is a dashboard admin. Manage it from Dashboard IAM.",
  CANNOT_REMOVE_OWN_IAM: "You cannot remove your own Dashboard IAM access.",
  CANNOT_DEMOTE_SELF: "You cannot demote your own account.",
  LAST_IAM_HOLDER: "That would leave nobody with Dashboard IAM access.",
  ALREADY_EXISTS: "An account with that email already exists.",
  USERNAME_TAKEN: "That username is taken.",
  EMAIL_TAKEN: "That email is taken.",
  NO_CREDENTIAL_ACCOUNT: "That account signs in without a password, so there is no password to set.",
  VALIDATION: "The account service refused the request body.",
  UNKNOWN: "The account service failed. Try again, and tell the platform team if it repeats.",
  ET_AUTH_UNREACHABLE: "The account service did not answer. Try again.",
  ACCOUNT_TYPE_NOT_ALLOWED: "Admin accounts are promoted and demoted from Dashboard IAM.",
  DEMO_NEEDS_TRIAL: "Use Manage trial to make an account demo. It starts the trial in the same step.",
};

/** The sentence for an error body: the code's sentence, else the detail, else the code. */
export const etAuthErrorMessage = (
  body: { error?: unknown; detail?: unknown } | null | undefined,
  fallback: string,
): string => {
  const code = typeof body?.error === "string" ? body.error : undefined;
  if (code && ET_AUTH_ERROR_MESSAGES[code]) return ET_AUTH_ERROR_MESSAGES[code] as string;
  const detail = typeof body?.detail === "string" ? body.detail : undefined;
  return detail || code || fallback;
};

export const readEtAuthError = async (res: Response, fallback: string): Promise<string> => {
  try {
    return etAuthErrorMessage((await res.json()) as { error?: unknown; detail?: unknown }, fallback);
  } catch {
    return fallback;
  }
};
